import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import axios from "../api";
import { getAuthRedirectUrl } from "../utils/authRedirect";

function qrUrlFromValue(value) {
    const payload = encodeURIComponent(String(value));
    return `https://api.qrserver.com/v1/create-qr-code/?size=320x320&data=${payload}`;
}

const pad2 = (n) => String(n).padStart(2, "0");
const localYMD = (d = new Date()) =>
    `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

function passStatusLabel(p) {
    const st = String(p?.status || "").toLowerCase();
    const today = localYMD();
    const start = String(p?.start_date || "");
    const end = String(p?.end_date || "");
    if (st === "unactivated" || !start || !end) return "Ready to Activate";
    if (today < start) return "Not Redeemable";
    if (today > end) return "Expired";
    return "Active";
}

function isAdultPass(pass) {
    const holderType = String(pass?.holder_type || "adult").toLowerCase();
    const audience = String(pass?.pass_types?.audience || "").toLowerCase();

    return holderType !== "child" && audience !== "child";
}

function isPurchaserViewOnly(pass) {
    return pass?.managed_outbound === true || String(pass?.access_role || "").toLowerCase() === "purchaser_view";
}

function pickCurrentPass(passes) {
    if (!Array.isArray(passes) || passes.length === 0) return null;

    const adultPasses = passes.filter((pass) => isAdultPass(pass) && !isPurchaserViewOnly(pass));
    const candidates = adultPasses.length ? adultPasses : passes.filter(isAdultPass);

    if (candidates.length === 0) return null;

    const today = localYMD();

    const byCreatedOldest = (a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0);
    const byCreatedNewest = (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0);

    const active = candidates
        .filter((p) => p?.start_date && p?.end_date && p.start_date <= today && today <= p.end_date)
        .sort(byCreatedOldest)[0];
    if (active) return active;

    const unactivated = candidates
        .filter((p) => {
            const st = String(p?.status || "").toLowerCase();
            return st === "unactivated" || !p?.start_date || !p?.end_date;
        })
        .sort(byCreatedOldest)[0];
    if (unactivated) return unactivated;

    const upcoming = candidates
        .filter((p) => p?.start_date && today < p.start_date)
        .sort((a, b) => String(a.start_date).localeCompare(String(b.start_date)) || byCreatedOldest(a, b))[0];
    if (upcoming) return upcoming;

    return (
        candidates
            .filter((p) => p?.end_date && today > p.end_date)
            .sort((a, b) => String(b.end_date).localeCompare(String(a.end_date)) || byCreatedNewest(a, b))[0] || null
    );
}

export default function AccountApp() {
    const [user, setUser] = useState(null);
    const [passes, setPasses] = useState([]);
    const [status, setStatus] = useState("");
    const [busy, setBusy] = useState(false);

    const nav = useNavigate();

    useEffect(() => {
        const init = async () => {
            const { data } = await supabase.auth.getSession();
            if (data?.session?.user) {
                setUser(data.session.user);
                fetchPasses();
            }
        };
        init();

        const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
            if (session?.user) {
                setUser(session.user);
                fetchPasses();
            } else {
                setUser(null);
                setPasses([]);
            }
        });

        return () => {
            sub?.subscription?.unsubscribe?.();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchPasses = async () => {
        try {
            const { data } = await supabase.auth.getSession();
            const token = data?.session?.access_token;

            if (!token) {
                setPasses([]);
                return;
            }

            const res = await axios.get("/passes", {
                headers: { Authorization: `Bearer ${token}` },
            });

            setPasses(res.data || []);
        } catch (err) {
            console.error("Error fetching passes", err?.response?.data || err);
            setStatus("Could not load your pass.");
        }
    };

    const currentPass = useMemo(() => pickCurrentPass(passes), [passes]);
    const badgeText = currentPass ? passStatusLabel(currentPass) : "No Pass";
    const badgeClass =
        badgeText === "Active"
            ? "active"
            : badgeText === "Expired" || badgeText === "Trip Window Passed"
                ? "expired"
                : badgeText === "Not Redeemable" || badgeText === "Not Redeemable Yet" || badgeText === "Ready to Activate"
                    ? "scheduled"
                    : "idle";
    const passQrValue = useMemo(() => (currentPass ? currentPass.id : null), [currentPass]);

    const handleLogout = async () => {
        setStatus("");
        setBusy(true);
        try {
            const { error } = await supabase.auth.signOut({ scope: "local" });
            if (error) console.warn("Logout (local) error:", error.message);
            window.location.href = "/";
        } finally {
            setBusy(false);
        }
    };

    const resetWalkthrough = async () => {
        const { data } = await supabase.auth.getSession();
        const userId = data?.session?.user?.id;
        if (userId) localStorage.removeItem(`bbp_onboarded_v1_${userId}`);
        setStatus("Walkthrough reset. Go back to Home to see it again.");
    };

    const copyPassId = async () => {
        if (!currentPass?.id) return;
        try {
            await navigator.clipboard.writeText(currentPass.id);
            setStatus("Copied pass ID.");
        } catch {
            setStatus("Could not copy pass ID.");
        }
    };

    const sharePassId = async () => {
        if (!currentPass?.id) return;
        const text = `Beach Bum Pass ID: ${currentPass.id}`;
        try {
            if (navigator.share) {
                await navigator.share({ title: "Beach Bum Pass", text });
                setStatus("Shared.");
                return;
            }
        } catch {
        }
        await copyPassId();
    };

    const exportPassPdf = async () => {
        if (!currentPass?.id) return;

        setStatus("");
        setBusy(true);
        try {
            const { data } = await supabase.auth.getSession();
            const token = data?.session?.access_token;
            if (!token) {
                setStatus("Please log in again.");
                return;
            }

            const res = await axios.get(`/passes/${currentPass.id}/pdf`, {
                responseType: "blob",
                headers: { Authorization: `Bearer ${token}` },
            });

            const url = URL.createObjectURL(res.data);
            window.open(url, "_blank");
            setTimeout(() => URL.revokeObjectURL(url), 60000);
            setStatus("Pass PDF opened.");
        } catch (e) {
            console.error("PDF export error:", e?.response?.data || e);
            setStatus("Could not export pass PDF.");
        } finally {
            setBusy(false);
        }
    };



    const passesBasePath = "/my-pass";
    const pageTitle = "My Account";
    const pageSubtitle = "Your pass, your crew, and your account controls — cleaned up and packed in one beach bag.";

    const SettingsItem = ({ title, sub, right, onClick, danger = false, disabled = false, icon = "⚙️" }) => {
        const Tag = onClick ? "button" : "div";
        return (
            <Tag
                className={`settings-item ${onClick ? "btnlike" : ""}`}
                onClick={onClick}
                disabled={disabled}
                type={onClick ? "button" : undefined}
                style={danger ? { borderColor: "rgba(255,107,107,0.26)" } : undefined}
            >
                <div className="settings-item-left settings-item-left-row">
                    <div className={`settings-item-icon ${danger ? "danger" : ""}`}>{icon}</div>
                    <div>
                        <div className="settings-item-title">{title}</div>
                        {sub ? <div className="settings-item-sub">{sub}</div> : null}
                    </div>
                </div>
                <div className="settings-item-right">{right ? right : <span className="settings-chevron">›</span>}</div>
            </Tag>
        );
    };

    if (!user) {
        return (
            <div className="settings-shell settings-shell--app">
                <header className="app-page-hero app-page-hero--account account-app-overview-hero">
                    <div className="app-page-kicker"><span aria-hidden="true">👤</span>Account</div>
                    <h1>My Account</h1>
                    <p>Log in to manage your pass, your crew, and your coastal game plan.</p>
                </header>

                <div className="settings-card account-auth-card">
                    <div className="settings-title">Log in required</div>
                    <div className="settings-sub">You need an account to manage your pass and preferences.</div>
                    <div className="passes-actions">
                        <button className="app-btn accent" type="button" onClick={() => nav("/login")}>Log In</button>
                        <button className="app-btn secondary" type="button" onClick={() => nav("/register")}>Create Account</button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="settings-shell settings-shell--app">
            <header className="app-page-hero app-page-hero--account account-app-overview-hero">
                <div className="app-page-kicker"><span aria-hidden="true">👤</span>Account</div>
                <h1>{pageTitle}</h1>
                <p>{pageSubtitle}</p>
                <div className="account-app-signed-in">
                    <span className="account-app-signed-in__avatar" aria-hidden="true">{String(user.email || "U").slice(0, 1).toUpperCase()}</span>
                    <span className="account-app-signed-in__label">Signed in</span>
                    <span className="account-app-signed-in__email">{user.email}</span>
                </div>
            </header>

            {status ? <div className="settings-toast">{status}</div> : null}

            <div className="account-app-stack">
                <div className="settings-card account-pass-card account-pass-card--app">
                    <div className="account-pass-card__top">
                        <div>
                            <div className="account-pass-card__eyebrow">Beach Bum Pass</div>
                            <div className="account-pass-card__title">{currentPass ? "" : "No pass in your beach bag yet."}</div>
                            <div className="account-pass-card__copy">
                                {currentPass
                                    ? ""
                                    : "No pass in the beach bag yet. Redeem an invite or build your pass when you're ready to cast off."}
                            </div>
                        </div>

                        <div className={`account-pass-status account-pass-status--${badgeClass}`}>
                            {badgeText}
                        </div>
                    </div>

                    {currentPass ? (
                        <div className="account-pass-card__grid">
                            <div className="account-pass-card__body">
                                <div className="account-pass-card__hero">
                                    <div className="account-pass-card__hero-name">{currentPass.pass_types?.name || "Beach Bum Pass"}</div>
                                    <div className="account-pass-card__hero-sub">{currentPass.start_date && currentPass.end_date ? `${currentPass.start_date} → ${currentPass.end_date}` : "Activates on first scan"}</div>
                                </div>

                                <div className="account-pass-stats">
                                    <div className="account-pass-stat">
                                        <span>Duration</span>
                                        <strong>{currentPass.pass_types?.duration_days ?? "—"} days</strong>
                                    </div>
                                    <div className="account-pass-stat">
                                        <span>Pass ID</span>
                                        <strong className="mono">{String(currentPass.id).slice(0, 8)}…</strong>
                                    </div>
                                </div>

                                <div className="account-pass-actions">
                                    <button className="app-btn secondary" onClick={copyPassId} disabled={busy}>Copy Pass ID</button>
                                    <button className="app-btn secondary" onClick={sharePassId} disabled={busy}>Share Pass ID</button>
                                    <button className="app-btn secondary" onClick={exportPassPdf} disabled={busy}>Export PDF</button>
                                </div>
                            </div>

                            <div className="account-pass-card__qr">
                                <div className="account-pass-card__qr-shell"><img className="pass-qr" alt="Pass QR Code" src={qrUrlFromValue(passQrValue)} /></div>
                                <div className="pass-qr-caption">Scan this at your first activity to activate your pass.</div>
                            </div>
                        </div>
                    ) : (
                        <div className="account-empty-callout">
                            <div className="account-empty-callout__title">No pass yet</div>
                            <div className="account-empty-callout__copy">You don't have an active pass yet. Unlock an invite or head to the pass page when you're ready to chart one.</div>
                            <div className="account-empty-callout__actions"><button className="app-btn accent" onClick={() => nav("/my-pass")}>Chart a Pass</button></div>
                        </div>
                    )}
                </div>

                <div className="settings-card">
                    <div className="settings-title">Pass Management</div>
                    <div className="settings-list">
                        <SettingsItem icon="🎟️" title="Redeem Pass" sub="Claim a pass that was emailed to you" onClick={() => nav(`${passesBasePath}#claim`)} />
                        <SettingsItem icon="🧒" title="Crew Passes" sub="View and assign Grommet and Little Bum passes attached to your account" onClick={() => nav(`${passesBasePath}#child-passes`)} />
                    </div>
                </div>

                <div className="settings-card">
                    <div className="settings-title">Profile</div>
                    <div className="settings-list">
                        <SettingsItem icon="👤" title="Profile Management" sub="Update the basics attached to your Beach Bum account" onClick={() => nav("/account/profile")} />
                    </div>
                </div>

                <div className="settings-card">
                    <div className="settings-title">Help</div>
                    <div className="settings-list">
                        <SettingsItem icon="ℹ️" title="How it Works" sub="Pass activation, reservations, and scan rules" onClick={() => nav("/how-it-works")} />
                    </div>
                </div>

                <div className="settings-card">
                    <div className="settings-title">Security & Login</div>
                    <div className="settings-list">
                        <SettingsItem
                            icon="🔐"
                            title="Change Password"
                            sub="Send yourself a reset link"
                            disabled={busy}
                            onClick={async () => {
                                setStatus("");
                                setBusy(true);
                                try {
                                    const cleanEmail = user.email;
                                    const { error } = await supabase.auth.resetPasswordForEmail(cleanEmail, { redirectTo: getAuthRedirectUrl("/auth/update-password") });
                                    if (error) return setStatus(error.message);
                                    setStatus("Password reset email sent.");
                                } finally {
                                    setBusy(false);
                                }
                            }}
                        />
                    </div>
                </div>

                <div className="settings-card">
                    <div className="settings-title">Privacy & Data</div>
                    <div className="settings-list">
                        <SettingsItem icon="🧾" title="Export Pass PDF" sub="Save a copy of your current pass" onClick={exportPassPdf} disabled={busy || !currentPass} right={<span className="settings-chevron">›</span>} />
                        <SettingsItem icon="🧷" title="Copy / Share Pass ID" sub="Quickly share your pass code" onClick={sharePassId} disabled={busy || !currentPass} />
                    </div>
                </div>

                <div className="settings-card">
                    <div className="settings-title">Account Actions</div>
                    <div className="settings-list">
                        <SettingsItem icon="🧭" title="Reset Walkthrough" sub="Replay the welcome walkthrough" onClick={resetWalkthrough} disabled={busy} />
                        <SettingsItem icon="🚪" title="Log Out" sub="Sign out of this device" onClick={handleLogout} disabled={busy} />
                    </div>
                </div>
            </div>


        </div>
    );
}